import { Badge } from '@/components/ui/badge';
import type { BudgetPacingStatus } from '@/services/googleAdsMonthlyService';

// Shared budget-pacing status copy + badge, lifted out of BudgetReallocationTab
// so the MCC Account Overview can render the same badge. Single source of truth
// for pacing-status labels + coral/teal theme classes.
export const budgetStatusCopy: Record<
  BudgetPacingStatus,
  { label: string; className: string }
> = {
  no_data: {
    label: 'No data',
    className: 'border-border bg-warm-bg text-muted-foreground',
  },
  on_track: {
    label: 'On track',
    className: 'border-teal/30 bg-teal-light text-teal-dark',
  },
  over_pacing: {
    label: 'Over pacing',
    className: 'border-coral/40 bg-coral-light text-coral-dark',
  },
  under_pacing: {
    label: 'Under pacing',
    className: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300',
  },
  over_spend: {
    label: 'Over budget',
    className: 'border-destructive/30 bg-destructive/10 text-destructive',
  },
};

export function BudgetPacingBadge({ status }: { status: BudgetPacingStatus }) {
  const copy = budgetStatusCopy[status] ?? budgetStatusCopy.no_data;
  return (
    <Badge variant="outline" className={copy.className}>
      {copy.label}
    </Badge>
  );
}

const CHANNEL_LABELS: Record<string, string> = {
  SEARCH: 'Search',
  PERFORMANCE_MAX: 'PMax',
  DISPLAY: 'Display',
  LOCAL_SERVICES: 'LSA',
  DEMAND_GEN: 'Demand Gen',
  SHOPPING: 'Shopping',
  VIDEO: 'Video',
  SMART: 'Smart',
};

export function campaignChannelLabel(channel: string | undefined): string {
  const key = (channel || 'SEARCH').toUpperCase();
  return CHANNEL_LABELS[key] || key.replace(/_/g, ' ').toLowerCase();
}

export function isSearchChannel(channel: string | undefined): boolean {
  return (channel || 'SEARCH').toUpperCase() === 'SEARCH';
}

export type LiveChannelBucket =
  | 'SEARCH'
  | 'PERFORMANCE_MAX'
  | 'LOCAL_SERVICES'
  | 'OTHER';

export function liveChannelBucket(
  channel: string | undefined,
): LiveChannelBucket {
  const key = (channel || 'SEARCH').toUpperCase();
  if (key === 'SEARCH') return 'SEARCH';
  if (key === 'PERFORMANCE_MAX') return 'PERFORMANCE_MAX';
  if (key === 'LOCAL_SERVICES') return 'LOCAL_SERVICES';
  return 'OTHER';
}

export interface LiveChannelMoney {
  total: number;
  search: number;
  pmax: number;
  lsa: number;
  other: number;
}

export function emptyLiveChannelMoney(): LiveChannelMoney {
  return { total: 0, search: 0, pmax: 0, lsa: 0, other: 0 };
}

export function sumLiveChannelMoney(
  rows: Array<{ advertising_channel_type?: string; [key: string]: unknown }>,
  field: 'spend_usd' | 'daily_budget_usd' | 'remaining_usd' | 'since_cron_spend_usd',
): LiveChannelMoney {
  const out = emptyLiveChannelMoney();
  for (const row of rows) {
    const amount = Number(row[field]);
    if (!Number.isFinite(amount)) continue;
    out.total += amount;
    const bucket = liveChannelBucket(
      typeof row.advertising_channel_type === 'string'
        ? row.advertising_channel_type
        : undefined,
    );
    if (bucket === 'SEARCH') out.search += amount;
    else if (bucket === 'PERFORMANCE_MAX') out.pmax += amount;
    else if (bucket === 'LOCAL_SERVICES') out.lsa += amount;
    else out.other += amount;
  }
  return out;
}

/**
 * Mon/Thu 08:00 Central Time (America/Chicago), matching the backend constant
 * AUTONOMOUS_PACING_CRON_TZ in budget_pacing_service.py. A constant on both
 * sides on purpose: an env override on one side would let the card lie about
 * the tick time. DST-aware: the same civil hour is 13:00 UTC in summer and
 * 14:00 UTC in winter.
 */
export const AUTONOMOUS_PACING_CRON_TZ = 'America/Chicago';
export const AUTONOMOUS_PACING_CRON_HOUR = 8;

export interface NextAutonomousCron {
  at: Date;
  days: number;
  weekday: 'Mon' | 'Thu';
}

export interface LastAutonomousCron {
  at: Date;
  weekday: 'Mon' | 'Thu';
}

/** Minutes the zone's wall clock is ahead of UTC at `at` (negative in the Americas). */
function zoneOffsetMinutes(at: Date, timeZone: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(at)
      .map((part) => [part.type, part.value]),
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/** UTC instant of the cron hour on a civil date in the cron zone. */
function cronInstantOn(year: number, monthIndex: number, day: number): Date {
  const guess = Date.UTC(year, monthIndex, day, AUTONOMOUS_PACING_CRON_HOUR, 0, 0);
  // Two passes so a DST change between the guess and the answer lands right.
  let instant = guess - zoneOffsetMinutes(new Date(guess), AUTONOMOUS_PACING_CRON_TZ) * 60_000;
  instant = guess - zoneOffsetMinutes(new Date(instant), AUTONOMOUS_PACING_CRON_TZ) * 60_000;
  return new Date(instant);
}

function civilDateInCronZone(now: Date): { year: number; monthIndex: number; day: number } {
  const [year, month, day] = calendarDateInTimeZone(now, AUTONOMOUS_PACING_CRON_TZ)
    .split('-')
    .map(Number);
  return { year, monthIndex: month - 1, day };
}

function cronOnCivilOffset(now: Date, offsetDays: number): { at: Date; dow: number } {
  const { year, monthIndex, day } = civilDateInCronZone(now);
  const civil = new Date(Date.UTC(year, monthIndex, day + offsetDays));
  return {
    at: cronInstantOn(civil.getUTCFullYear(), civil.getUTCMonth(), civil.getUTCDate()),
    dow: civil.getUTCDay(),
  };
}

export function lastAutonomousCron(now: Date = new Date()): LastAutonomousCron {
  for (let offset = 0; offset <= 8; offset += 1) {
    const { at, dow } = cronOnCivilOffset(now, -offset);
    if ((dow === 1 || dow === 4) && at.getTime() <= now.getTime()) {
      return { at, weekday: dow === 1 ? 'Mon' : 'Thu' };
    }
  }
  return { at: cronOnCivilOffset(now, -7).at, weekday: 'Mon' };
}

export function nextAutonomousCron(now: Date = new Date()): NextAutonomousCron {
  for (let offset = 0; offset <= 8; offset += 1) {
    const { at, dow } = cronOnCivilOffset(now, offset);
    if ((dow === 1 || dow === 4) && at.getTime() > now.getTime()) {
      return {
        at,
        days: (at.getTime() - now.getTime()) / 86_400_000,
        weekday: dow === 1 ? 'Mon' : 'Thu',
      };
    }
  }
  const fallback = cronOnCivilOffset(now, 7).at;
  return {
    at: fallback,
    days: (fallback.getTime() - now.getTime()) / 86_400_000,
    weekday: 'Mon',
  };
}

/** "CDT" / "CST" for the tick instant, so labels read in the zone the cron runs in. */
export function cronZoneLabel(at: Date): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: AUTONOMOUS_PACING_CRON_TZ,
      timeZoneName: 'short',
    })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part?.value || 'CT';
  } catch {
    return 'CT';
  }
}

export function hoursLeftInUtcDay(now: Date = new Date()): number {
  const nextMidnight = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
  );
  return Math.max(0, (nextMidnight - now.getTime()) / 3_600_000);
}

/** Hours left in the Google Ads account's current civil day. */
export function hoursLeftInTimeZone(
  now: Date,
  timeZone?: string | null,
): number {
  if (!timeZone) return hoursLeftInUtcDay(now);
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hourCycle: 'h23',
      })
        .formatToParts(now)
        .map((part) => [part.type, part.value]),
    );
    const hour = Number(parts.hour);
    const minute = Number(parts.minute);
    const second = Number(parts.second);
    if (![hour, minute, second].every(Number.isFinite)) {
      return hoursLeftInUtcDay(now);
    }
    return Math.max(0, 24 - hour - minute / 60 - second / 3600);
  } catch {
    return hoursLeftInUtcDay(now);
  }
}

export function formatDaysLeft(days: number): string {
  if (!Number.isFinite(days) || days < 0) return '—';
  if (days >= 1 && Number.isInteger(days)) {
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (days >= 1) return `${days.toFixed(1)} days`;
  const hours = days * 24;
  if (hours >= 1) return `${Math.round(hours)}h`;
  return `${Math.max(1, Math.round(hours * 60))}m`;
}

/** Civil YYYY-MM-DD in the Google Ads account timezone (UTC if missing). */
export function calendarDateInTimeZone(
  now: Date,
  timeZone?: string | null,
): string {
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone || 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
        .formatToParts(now)
        .map((part) => [part.type, part.value]),
    );
    if (parts.year && parts.month && parts.day) {
      return `${parts.year}-${parts.month}-${parts.day}`;
    }
  } catch {
    // Invalid IANA zone — fall through to UTC date.
  }
  return now.toISOString().slice(0, 10);
}

/** Inclusive days from today (account TZ) through cycle end. Null if no date. */
export function daysLeftInCycleWindow(
  cycleEnd: string | undefined,
  now: Date,
  timeZone?: string | null,
): number | null {
  const end = (cycleEnd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return null;
  const today = calendarDateInTimeZone(now, timeZone);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  const todayMs = Date.parse(`${today}T00:00:00Z`);
  if (!Number.isFinite(endMs) || !Number.isFinite(todayMs)) return null;
  return Math.round((endMs - todayMs) / 86_400_000) + 1;
}

export function formatCycleDaysLeft(
  days: number | null | undefined,
): string {
  if (days == null || !Number.isFinite(days)) return '—';
  if (days <= 0) return 'Cycle ended';
  return formatDaysLeft(days);
}

export function scaleLiveChannelMoney(
  money: LiveChannelMoney,
  factor: number,
): LiveChannelMoney {
  if (!Number.isFinite(factor) || factor <= 0) return emptyLiveChannelMoney();
  return {
    total: money.total * factor,
    search: money.search * factor,
    pmax: money.pmax * factor,
    lsa: money.lsa * factor,
    other: money.other * factor,
  };
}

/** KPI cards use Campaign Creator's saved monthly only when the tenant is not
 *  Planner-mapped, a cycle snapshot exists, and live Ads returned no campaign
 *  budgets. Mapped clients keep Planner remaining even if SEARCH is empty. */
export function usesCampaignCreatorBudgetFallback(args: {
  hasCurrentCycle: boolean;
  plannerSourced: boolean;
  hasLiveBudget: boolean;
  defaultMonthlyBudgetUsd: number;
}): boolean {
  return Boolean(
    args.hasCurrentCycle &&
      !args.plannerSourced &&
      !args.hasLiveBudget &&
      args.defaultMonthlyBudgetUsd > 0,
  );
}

export const LSA_MIN_WEEKLY_MICROS = 1_000_000;

/**
 * Whole-dollar weekly LSA budget for an operator to type into Google.
 *
 * Mirrors `lsa_weekly_recommendation_micros` in
 * `backend/api/services/google_ads/budget_pacing_apply_service.py`. Two
 * producers feed the LSA row (the apply response and the Budgets snapshot),
 * so the math lives here once rather than in each of them.
 *
 * The display recommendation from Planner spend. The LSA budget writer
 * (backend, lsa_budget_writer.py) computes its own figure from live spend.
 *
 * Floors at $1/week rather than $0 on every degenerate path, and caps at the
 * leftover itself so a sub-week remainder cannot authorise a larger weekly
 * spend than remains ($100 across 3 days is $233 unclamped). The cap only
 * binds when daysLeft < 7.
 */
export function lsaWeeklyRecommendationMicros(
  remainingMicros: number | null | undefined,
  daysLeft: number | null | undefined,
): number {
  const remaining = typeof remainingMicros === 'number' && Number.isFinite(remainingMicros)
    ? remainingMicros
    : 0;
  const days = typeof daysLeft === 'number' && Number.isFinite(daysLeft) ? daysLeft : 0;
  if (days <= 0 || remaining <= 0) return LSA_MIN_WEEKLY_MICROS;
  const weeklyMicros = Math.min((remaining / days) * 7, remaining);
  const wholeDollars = Math.round(weeklyMicros / 1_000_000);
  return Math.max(LSA_MIN_WEEKLY_MICROS, wholeDollars * 1_000_000);
}
